// Casos (objeto caso de la app): los tickets de soporte y de los buzones de grupo. Se abren solos con cada correo
// nuevo (funcion casos-desde-correo), a mano o desde la migracion de Zammad.
import { buscarEnCasos, enOrden } from './busqueda';
import { gqlTodos } from './gql';
import { ApiError, archivoConSesion, request } from './http';
import { create, f, listAll, update } from './rest';

export type EstadoCaso = 'NUEVO' | 'ABIERTO' | 'PENDIENTE' | 'RESUELTO' | 'CERRADO';
export type PrioridadCaso = 'BAJA' | 'NORMAL' | 'ALTA' | 'URGENTE';
export type NotaInterna = { autor: string; fecha: string; texto: string };

export type Caso = {
  id: string;
  numero: number | null;
  asunto: string;
  estado: EstadoCaso;
  prioridad: PrioridadCaso;
  grupo: string;
  asignadoId: string | null;
  duenoZammad: string;
  hiloId: string | null;
  hilosUnidos: string[];
  correoCliente: string;
  origen: string;
  // WhatsApp (D73): conversacion de Zernio del caso.
  whatsappConversacion?: string | null;
  etiquetas: string[];
  ultimoMensaje: string | null;
  primeraRespuesta: string | null;
  cerradoEn: string | null;
  // Quien escribio ultimo y cuando volver a perseguir al cliente (D37).
  ultimoCliente: string | null;
  ultimoEquipo: string | null;
  seguimientoEn: string | null;
  // Lo que deja el asistente de IA (entrega 2); vacio mientras no este activo.
  resumenIA: string;
  siguientePasoIA: string;
  respuestaIA: string;
  zammadId: number | null;
  notas: NotaInterna[];
  personaId: string | null;
  empresaId: string | null;
  persona: { id: string; nombre: string } | null;
  empresa: { id: string; nombre: string } | null;
  creado: string;
};

export type RawCaso = {
  id: string;
  name: string;
  numero: number | null;
  estado: EstadoCaso | null;
  prioridad: PrioridadCaso | null;
  grupo: string | null;
  asignadoId: string | null;
  duenoZammad?: string | null;
  hiloId: string | null;
  hilosUnidos?: string[] | null;
  correoCliente: string | null;
  origen: string | null;
  whatsappConversacion?: string | null;
  etiquetas: string[] | null;
  ultimoMensaje: string | null;
  primeraRespuesta: string | null;
  cerradoEn: string | null;
  ultimoCliente?: string | null;
  ultimoEquipo?: string | null;
  seguimientoEn?: string | null;
  resumenIA?: string | null;
  siguientePasoIA?: string | null;
  respuestaIA?: string | null;
  zammadId: number | null;
  notasInternas: NotaInterna[] | null;
  personaId: string | null;
  empresaId: string | null;
  persona?: { id: string; name?: { firstName?: string; lastName?: string } } | null;
  empresa?: { id: string; name?: string } | null;
  createdAt: string;
};

export const aCaso = (r: RawCaso): Caso => ({
  id: r.id,
  numero: r.numero,
  asunto: r.name || '(sin asunto)',
  estado: r.estado ?? 'NUEVO',
  prioridad: r.prioridad ?? 'NORMAL',
  grupo: r.grupo ?? '',
  asignadoId: r.asignadoId,
  duenoZammad: r.duenoZammad ?? '',
  hiloId: r.hiloId,
  hilosUnidos: r.hilosUnidos ?? [],
  correoCliente: r.correoCliente ?? '',
  origen: r.origen ?? 'CORREO',
  whatsappConversacion: r.whatsappConversacion || null,
  etiquetas: r.etiquetas ?? [],
  ultimoMensaje: r.ultimoMensaje,
  primeraRespuesta: r.primeraRespuesta,
  cerradoEn: r.cerradoEn,
  ultimoCliente: r.ultimoCliente ?? null,
  ultimoEquipo: r.ultimoEquipo ?? null,
  seguimientoEn: r.seguimientoEn ?? null,
  resumenIA: r.resumenIA ?? '',
  siguientePasoIA: r.siguientePasoIA ?? '',
  respuestaIA: r.respuestaIA ?? '',
  zammadId: r.zammadId,
  notas: Array.isArray(r.notasInternas) ? r.notasInternas : [],
  personaId: r.personaId,
  empresaId: r.empresaId,
  persona: r.persona ? { id: r.persona.id, nombre: `${r.persona.name?.firstName ?? ''} ${r.persona.name?.lastName ?? ''}`.trim() || 'Contacto' } : null,
  empresa: r.empresa ? { id: r.empresa.id, nombre: r.empresa.name ?? 'Empresa' } : null,
  creado: r.createdAt,
});

// Casos abiertos (no cerrados) y los cerrados con movimiento en los ultimos 30 dias. Se mira el ultimo mensaje, no
// updatedAt: la migracion de Zammad toco todos los casos viejos y los traeria todos.
const CAMPOS_CASO =
  'id name numero estado prioridad grupo asignadoId duenoZammad hiloId hilosUnidos correoCliente origen whatsappConversacion etiquetas ultimoMensaje primeraRespuesta cerradoEn ultimoCliente ultimoEquipo seguimientoEn resumenIA siguientePasoIA respuestaIA zammadId notasInternas personaId empresaId createdAt persona{ id name{ firstName lastName } } empresa{ id name }';

export async function casos() {
  const desde = new Date(Date.now() - 30 * 86_400_000).toISOString();
  const items = await gqlTodos<RawCaso>('casos', CAMPOS_CASO, { filtro: { or: [{ estado: { neq: 'CERRADO' } }, { ultimoMensaje: { gte: desde } }] }, orden: [{ ultimoMensaje: 'DescNullsLast' }] });
  return items.map(aCaso);
}

export const casosDe = async (campo: 'personaId' | 'empresaId', id: string) =>
  (await listAll<RawCaso>('casos', { filter: f.eq(campo, id), orderBy: 'createdAt[DescNullsLast]', depth: 1 }, 200)).map(aCaso);

export type CambiosCaso = Partial<{
  // Titulo del caso (D51): solo en el CRM; el asunto de los correos se elige al enviar.
  name: string;
  estado: EstadoCaso;
  prioridad: PrioridadCaso;
  asignadoId: string | null;
  duenoZammad: string;
  hiloId: string | null;
  hilosUnidos: string[];
  hilosExcluidos: string[];
  grupo: string;
  etiquetas: string[];
  notasInternas: NotaInterna[];
  cerradoEn: string | null;
  // Hora del ultimo aviso de cierre (lo marca casos-desde-correo); la Consola lo pone al resolver sin avisar (D90).
  avisoCierre: string | null;
  seguimientoEn: string | null;
}>;

export const editarCaso = (id: string, c: CambiosCaso) => update('casos', id, c);

export const ESTADOS_CASO: [EstadoCaso, string, 'acc' | 'warn' | 'ok' | undefined][] = [
  ['NUEVO', 'Nuevo', 'acc'],
  ['ABIERTO', 'Abierto', 'acc'],
  ['PENDIENTE', 'Pendiente del cliente', 'warn'],
  ['RESUELTO', 'Resuelto', 'ok'],
  ['CERRADO', 'Cerrado', undefined],
];
export const PRIORIDADES: [PrioridadCaso, string][] = [
  ['BAJA', 'Baja'],
  ['NORMAL', 'Normal'],
  ['ALTA', 'Alta'],
  ['URGENTE', 'Urgente'],
];

/* ---------------- conversacion historica (tickets migrados de Zammad) ---------------- */

// messageIdOrigen / hiloOrigen / copiadoPorId: copia de un correo de un buzon personal unido al caso (capa 3: lo escrito
// con el cliente, para que lo vea todo el grupo). El dueno del buzon ve el original y no la copia (conversacionCaso).
export type MensajeCaso = {
  id: string;
  asunto: string;
  de: string;
  para: string;
  cc: string;
  fecha: string;
  texto: string;
  interno: boolean;
  entrante: boolean;
  zammadId: number | null;
  messageIdOrigen?: string | null;
  hiloOrigen?: string | null;
  copiadoPorId?: string | null;
  creado?: string;
};
type RawMensajeCaso = {
  id: string;
  name: string;
  de: string | null;
  para: string | null;
  cc?: string | null;
  fecha: string | null;
  texto: string | null;
  interno: boolean | null;
  entrante: boolean | null;
  zammadId?: number | null;
  messageIdOrigen?: string | null;
  hiloOrigen?: string | null;
  copiadoPorId?: string | null;
  createdAt: string;
};

export const mensajesDeCaso = async (casoId: string): Promise<MensajeCaso[]> =>
  (await listAll<RawMensajeCaso>('mensajesCaso', { filter: f.eq('casoId', casoId), orderBy: 'fecha[AscNullsLast]' }, 500)).map((m) => ({
    id: m.id,
    asunto: m.name,
    de: m.de ?? '',
    para: m.para ?? '',
    cc: m.cc ?? '',
    fecha: m.fecha ?? m.createdAt,
    texto: m.texto ?? '',
    interno: !!m.interno,
    entrante: m.entrante !== false,
    zammadId: m.zammadId ?? null,
    messageIdOrigen: m.messageIdOrigen ?? null,
    hiloOrigen: m.hiloOrigen ?? null,
    copiadoPorId: m.copiadoPorId ?? null,
    creado: m.createdAt,
  }));

/* ---------------- copias desde un buzon personal (capa 3) ---------------- */

// Las respuestas que la app copio al caso desde MI buzon personal en las ultimas 24 h (el aviso con "Deshacer"), una
// entrada por caso y hilo. La copia la hace casos-desde-correo; deshacerla es de quien es el buzon.
export const HORAS_DESHACER = 24;
export type CopiaReciente = { casoId: string; hiloId: string; numero: number | null; titulo: string; cliente: string; cuantas: number; fecha: string };
type RawCopia = { id: string; casoId: string; hiloOrigen: string | null; para: string | null; createdAt: string; caso: { id: string; numero: number | null; name: string; empresa: { name: string } | null } | null };

export async function misCopiasRecientes(miembroId: string, ahora = Date.now()): Promise<CopiaReciente[]> {
  const desde = new Date(ahora - HORAS_DESHACER * 3_600_000).toISOString();
  const filas = await gqlTodos<RawCopia>('mensajesCaso', 'id casoId hiloOrigen para createdAt caso{ id numero name empresa{ name } }', {
    filtro: { and: [{ copiadoPorId: { eq: miembroId } }, { createdAt: { gte: desde } }] },
    max: 200,
  });
  return agruparCopias(filas);
}

// Puro: una entrada por caso e hilo (dos respuestas del mismo hilo = un aviso). El cliente: la empresa del caso o, si
// no tiene, el primer destinatario de la copia.
export function agruparCopias(filas: RawCopia[]): CopiaReciente[] {
  const m = new Map<string, CopiaReciente>();
  for (const x of filas) {
    if (!x.hiloOrigen) continue;
    const k = `${x.casoId}:${x.hiloOrigen}`;
    const previo = m.get(k);
    const cliente = x.caso?.empresa?.name?.trim() || (x.para ?? '').split(',')[0]?.trim() || 'el cliente';
    if (previo) {
      previo.cuantas += 1;
      if (x.createdAt > previo.fecha) previo.fecha = x.createdAt;
    } else m.set(k, { casoId: x.casoId, hiloId: x.hiloOrigen, numero: x.caso?.numero ?? null, titulo: x.caso?.name ?? '', cliente, cuantas: 1, fecha: x.createdAt });
  }
  return [...m.values()].sort((a, b) => b.fecha.localeCompare(a.fecha));
}

// Deshacer (24 h): borra las copias de ese hilo, lo separa del caso y lo deja en hilosExcluidos para que no se vuelva
// a unir solo. Se lee el caso fresco: otra persona pudo cambiar sus hilos.
export async function deshacerCopia(casoId: string, hiloId: string, autor: string) {
  const c = (await gqlTodos<{ id: string; hilosUnidos: string[] | null; hilosExcluidos: string[] | null; notasInternas: NotaInterna[] | null }>('casos', 'id hilosUnidos hilosExcluidos notasInternas', { filtro: { id: { eq: casoId } }, max: 1 }))[0];
  if (!c) throw new Error('Ese caso ya no existe');
  const copias = await gqlTodos<{ id: string }>('mensajesCaso', 'id', { filtro: { and: [{ casoId: { eq: casoId } }, { hiloOrigen: { eq: hiloId } }] }, max: 200 });
  if (copias.length) await gql('mutation($f:MensajeCasoFilterInput!){ deleteMensajesCaso(filter:$f){ id } }', { f: { id: { in: copias.map((x) => x.id) } } });
  await update('casos', casoId, {
    hilosUnidos: (c.hilosUnidos ?? []).filter((h) => h !== hiloId),
    hilosExcluidos: [...new Set([...(c.hilosExcluidos ?? []), hiloId])],
    notasInternas: [...(Array.isArray(c.notasInternas) ? c.notasInternas : []), nota(autor, `Se deshizo la copia: ${copias.length === 1 ? 'se quitó 1 correo copiado' : `se quitaron ${copias.length} correos copiados`} de su buzón personal y se separó su correo de este caso.`)],
  });
  return copias.length;
}

// Respuesta enviada por correo desde la conversacion guardada del caso (casos de Zammad o sin correo): queda en el caso
// enseguida. Antes volvia sola porque salia por Zammad y la sincronizacion la traia; con Zammad desligado (D81) se
// guarda aqui. El correo enviado queda ademas en Enviados del buzon.
export async function guardarRespuestaEnCaso(casoId: string, r: { asunto: string; de: string; para: string[]; cc: string[]; texto: string }) {
  const ahora = new Date().toISOString();
  await create('mensajesCaso', { name: (r.asunto || '(sin asunto)').slice(0, 250), de: r.de, para: [...r.para, ...r.cc].join(', '), texto: r.texto, fecha: ahora, interno: false, entrante: false, casoId });
  await update('casos', casoId, { ultimoMensaje: ahora, ultimoEquipo: ahora });
}

// Adjuntos de un caso de Zammad (D42): la lista por articulo y cada archivo (Zammad los guarda; el CRM no los copia).
export type AdjuntoZammad = { id: number; nombre: string; tamano: number; tipo: string; enLinea: boolean; cid?: string | null };
// `textos`: el texto de los mensajes con imagenes pegadas, con la marca donde va cada una (los guardados antes no la
// tienen).
// Rapido (D88): directo del servicio de correo, que los tiene indexados en el bucket (la funcion de la app tardaba
// 20-70 s). Si el servicio aun no tiene la ruta (404) o el bucket (409), se usa la de la app como antes.
const usarLaApp = (e: unknown) => e instanceof ApiError && (e.status === 404 || e.status === 409);
export const adjuntosZammad = async (casoId: string) => {
  type R = { adjuntos: Record<string, AdjuntoZammad[]>; textos?: Record<string, string> };
  const r = await request<R>('/correo/api/zammad/adjuntos', { query: { caso: casoId } }).catch((e) => {
    if (usarLaApp(e)) return request<R>('/s/zammad/adjuntos', { query: { casoId } });
    throw e;
  });
  return { adjuntos: r.adjuntos ?? {}, textos: r.textos ?? {} };
};
// Etiqueta puesta o quitada desde el CRM en un caso de Zammad: tambien en el ticket (D44).
export const etiquetaZammad = (casoId: string, etiqueta: string, quitar: boolean) => request<{ ok: boolean }>('/s/zammad/etiqueta', { method: 'POST', body: { casoId, etiqueta, quitar } });

// Zammad desligado (D81, fase 2): desde el 25-oct-2026 Zammad ya no lee los buzones del CRM. Los casos que vinieron de
// Zammad se responden por el buzon del grupo (asunto con [Ticket#N]) y lo que llega despues por correo a sus hilos lo
// copia la app a la conversacion del caso (casos-desde-correo). Por eso abren siempre en su conversacion, que tiene todo:
// antes cambiaban solos a la vista de correo cuando llegaba algo y ahi no se veian las respuestas enviadas desde el caso
// ni el correo de los hilos unidos ("se borro lo que respondi", caso #20261021620016). El boton deja ver el correo.
export const abreEnCorreo = (c: Pick<Caso, 'origen'>) => c.origen !== 'ZAMMAD';

// Desde que correo envia Zammad las respuestas de cada grupo (D43).
export const correosGruposZammad = async () => (await request<{ grupos: { grupo: string; correo: string }[] }>('/s/zammad/grupos')).grupos;

export async function archivoZammad(casoId: string, articulo: number, id: number) {
  try {
    return await archivoConSesion(`/correo/api/zammad/archivo?${new URLSearchParams({ caso: casoId, articulo: String(articulo), id: String(id) })}`);
  } catch (e) {
    if (!usarLaApp(e)) throw e;
  }
  const r = await request<{ tipo: string; datos: string }>('/s/zammad/adjuntos', { query: { casoId, articulo, id } });
  const bytes = Uint8Array.from(atob(r.datos), (ch) => ch.charCodeAt(0));
  return new Blob([bytes], { type: r.tipo });
}

// Responder un caso de Zammad desde el CRM (D40): sale por Zammad (a nombre de quien responde) y vuelve al caso enseguida.
export type RespuestaZammad = {
  casoId: string;
  tipo: 'email' | 'nota';
  para?: string[];
  cc?: string[];
  texto: string;
  responderA?: number | null;
  // Asunto del correo (D51); sin el, Zammad usa el titulo del ticket.
  asunto?: string;
  adjuntos?: { nombre: string; tipo: string; datos: string }[];
};
// Sincronizacion con Zammad (D41): estado de la ultima corrida y "Sincronizar ahora".
export type EstadoZammad = {
  configurada: boolean;
  corte: string | null;
  ultima: { en?: string; tickets?: number; pendientes?: boolean; creados?: number; mensajes?: number; error?: string; errores?: string[] } | null;
};
export const estadoZammad = () => request<EstadoZammad>('/s/zammad/estado');
// desde: vuelve a revisar desde esa fecha (ISO) lo que ya se dio por revisado.
export const sincronizarZammadAhora = (desde?: string) =>
  request<{ pendientes: boolean; creados: number; mensajes: number; errores?: string[] }>('/s/zammad/sincronizar', { method: 'POST', query: { desde } });
export const responderPorZammad = (r: RespuestaZammad) => request<{ articuloId: number | null; aNombre: boolean }>('/s/zammad/responder', { method: 'POST', body: r });

/* ---------------- SLA de primera respuesta ---------------- */

export const SLA_MINUTOS = 30;

// Minutos que lleva un caso activo esperando la primera respuesta del equipo; null si ya se respondio o no aplica
// (casos migrados de Zammad, pendientes del cliente o cerrados).
export function esperandoRespuesta(c: Pick<Caso, 'estado' | 'primeraRespuesta' | 'origen' | 'creado'>, ahora = Date.now()) {
  if (c.primeraRespuesta || c.origen === 'ZAMMAD' || (c.estado !== 'NUEVO' && c.estado !== 'ABIERTO')) return null;
  return Math.max(0, Math.floor((ahora - new Date(c.creado).getTime()) / 60_000));
}

export const textoEspera = (min: number) => (min < 60 ? `${min} min` : min < 1440 ? `${Math.floor(min / 60)} h` : `${Math.floor(min / 1440)} d`);

/* ---------------- reporte ---------------- */

// Casos que se abrieron o se cerraron desde una fecha, mas los que siguen abiertos (para la carga actual).
// Solo los campos que usa resumirCasos: con REST depth=1 venian todas las relaciones (mensajes con su texto,
// horas, movimientos de bolsa) y Twenty cortaba por tiempo (query read timeout -> 400 "Error").
const CAMPOS_REPORTE = 'id name numero estado prioridad grupo asignadoId origen primeraRespuesta cerradoEn seguimientoEn createdAt empresaId empresa{ id name }';
export async function casosParaReporte(desde: Date) {
  const d = desde.toISOString();
  const items = await gqlTodos<RawCaso>('casos', CAMPOS_REPORTE, {
    filtro: { or: [{ createdAt: { gte: d } }, { cerradoEn: { gte: d } }, { estado: { in: ['NUEVO', 'ABIERTO', 'PENDIENTE'] } }] },
    orden: [{ createdAt: 'DescNullsLast' }],
    max: 5000,
  });
  return items.map(aCaso);
}

/** Casos por id con los campos del reporte (para las horas de casos que no estan en el periodo). */
export async function casosPorIds(ids: string[]) {
  const unicos = [...new Set(ids)].filter(Boolean);
  const out: Caso[] = [];
  for (let i = 0; i < unicos.length; i += 200) {
    const items = await gqlTodos<RawCaso>('casos', CAMPOS_REPORTE, { filtro: { id: { in: unicos.slice(i, i + 200) } }, max: 200 });
    out.push(...items.map(aCaso));
  }
  return out;
}

export type ResumenCasos = {
  entraron: number;
  resueltos: number;
  abiertos: number;
  pendientes: number;
  respuestaMediana: number | null;
  dentroSla: number | null;
  porDia: number[];
  porGrupo: [string, number][];
  porEmpresa: [string, number][];
  cargaPorPersona: [string | null, number][];
};

const mediana = (xs: number[]) => {
  if (!xs.length) return null;
  const s = [...xs].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : Math.round((s[m - 1] + s[m]) / 2);
};
const contar = <K>(xs: K[]) => {
  const m = new Map<K, number>();
  for (const x of xs) m.set(x, (m.get(x) ?? 0) + 1);
  return [...m].sort((a, b) => b[1] - a[1]);
};

export function resumirCasos(lista: Caso[], desde: Date, hasta: Date): ResumenCasos {
  const en = (iso: string | null) => !!iso && new Date(iso) >= desde && new Date(iso) < hasta;
  const nuevos = lista.filter((c) => en(c.creado));
  const tiempos = nuevos.filter((c) => c.primeraRespuesta && c.origen !== 'ZAMMAD').map((c) => Math.max(0, (Date.parse(c.primeraRespuesta!) - Date.parse(c.creado)) / 60_000));
  const dias = Math.max(1, Math.round((hasta.getTime() - desde.getTime()) / 86_400_000));
  const porDia = Array.from({ length: dias }, () => 0);
  for (const c of nuevos) porDia[Math.min(dias - 1, Math.floor((Date.parse(c.creado) - desde.getTime()) / 86_400_000))] += 1;
  return {
    entraron: nuevos.length,
    resueltos: lista.filter((c) => (c.estado === 'RESUELTO' || c.estado === 'CERRADO') && en(c.cerradoEn)).length,
    abiertos: lista.filter((c) => c.estado === 'NUEVO' || c.estado === 'ABIERTO').length,
    pendientes: lista.filter((c) => c.estado === 'PENDIENTE').length,
    respuestaMediana: mediana(tiempos.map(Math.round)),
    dentroSla: tiempos.length ? Math.round((100 * tiempos.filter((t) => t <= SLA_MINUTOS).length) / tiempos.length) : null,
    porDia,
    porGrupo: contar(nuevos.map((c) => c.grupo || 'Sin grupo')),
    porEmpresa: contar(nuevos.map((c) => c.empresa?.nombre ?? 'Sin empresa')).slice(0, 10),
    cargaPorPersona: contar(lista.filter((c) => c.estado === 'NUEVO' || c.estado === 'ABIERTO').map((c) => c.asignadoId)),
  };
}

/* ---------------- unir casos y convertir correos (como en Zammad) ---------------- */

async function gql<T>(query: string, variables: Record<string, unknown>): Promise<T> {
  const r = await request<{ data?: T; errors?: { message: string }[] }>('/graphql', { method: 'POST', body: { query, variables } });
  if (r.errors?.length) throw new ApiError(400, r.errors[0].message);
  return r.data as T;
}

// Siguiente numero de caso en la serie propia del CRM (D65): AAAAMMDD + 9 + 5 digitos, el dia en hora de Colombia.
// Antes era "el mas alto + 1", que caia dentro de la serie de Zammad (AAAAMMDD + 62…) y chocaba con sus tickets.
export function rangoSerieCrm(fecha = new Date()) {
  // Regla de negocio: siempre Bogota (igual que el servidor), no la zona elegida en Mi perfil.
  const p = Object.fromEntries(new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Bogota', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(fecha).map((x) => [x.type, x.value]));
  const base = Number(`${p.year}${p.month}${p.day}900000`);
  return { desde: base + 1, hasta: base + 99_999 };
}

export async function siguienteNumero() {
  const { desde, hasta } = rangoSerieCrm();
  const d = await gql<{ casos: { edges: { node: { numero: number | null } }[] } }>('query($f: CasoFilterInput){ casos(filter: $f, orderBy:[{numero:DescNullsLast}], first:1){ edges{ node{ numero } } } }', {
    f: { and: [{ numero: { gte: desde } }, { numero: { lte: hasta } }] },
  });
  return Math.max(Number(d.casos.edges[0]?.node.numero ?? 0) + 1, desde);
}

const nota = (autor: string, texto: string): NotaInterna => ({ autor, fecha: new Date().toISOString(), texto });

// Une `origen` dentro de `destino`: pasan sus mensajes, horas y correos; el origen queda cerrado con una nota.
export async function unirCasos(origen: Caso, destino: Caso, autor: string) {
  if (origen.id === destino.id) throw new Error('Es el mismo caso');
  await gql('mutation($f:MensajeCasoFilterInput!,$d:MensajeCasoUpdateInput!){ updateMensajesCaso(filter:$f, data:$d){ id } }', { f: { casoId: { eq: origen.id } }, d: { casoId: destino.id } });
  await gql('mutation($f:RegistroHorasFilterInput!,$d:RegistroHorasUpdateInput!){ updateRegistrosHoras(filter:$f, data:$d){ id } }', {
    f: { casoId: { eq: origen.id } },
    d: { casoId: destino.id },
  }).catch(() => undefined);
  const hilos = [origen.hiloId, ...origen.hilosUnidos].filter((h): h is string => !!h);
  const hiloId = destino.hiloId ?? hilos.shift() ?? null;
  const hilosUnidos = [...new Set([...destino.hilosUnidos, ...hilos])].filter((h) => h !== hiloId);
  await editarCaso(destino.id, {
    hiloId,
    hilosUnidos,
    etiquetas: [...new Set([...destino.etiquetas, ...origen.etiquetas])],
    notasInternas: [...destino.notas, nota(autor, `Se unió el caso #${origen.numero ?? '—'} (${origen.asunto}).`)],
  });
  await editarCaso(origen.id, {
    estado: 'CERRADO',
    cerradoEn: new Date().toISOString(),
    hiloId: null,
    hilosUnidos: [],
    etiquetas: [...new Set([...origen.etiquetas, 'unido'])],
    notasInternas: [...origen.notas, nota(autor, `Unido al caso #${destino.numero ?? '—'} (${destino.asunto}).`)],
  });
}

// Un correo que no abrio caso (p. ej. del buzon de una persona) se convierte en caso.
export async function casoDesdeCorreo(d: { hiloId: string; asunto: string; correoCliente: string; grupo: string; asignadoId: string | null; personaId?: string | null }) {
  const numero = await siguienteNumero();
  const c = await create<{ id: string }>('casos', {
    name: d.asunto.trim() || '(sin asunto)',
    numero,
    estado: d.asignadoId ? 'ABIERTO' : 'NUEVO',
    prioridad: 'NORMAL',
    grupo: d.grupo,
    hiloId: d.hiloId,
    correoCliente: d.correoCliente,
    origen: 'CORREO',
    asignadoId: d.asignadoId,
    personaId: d.personaId ?? null,
    ultimoMensaje: new Date().toISOString(),
  });
  return { id: c.id, numero };
}

// Agrega un correo (hilo) a un caso existente. Si alguien lo habia separado (Deshacer), agregarlo a mano lo vuelve a
// permitir. Lo escrito con el cliente desde un buzon personal se copia solo al caso (casos-desde-correo, en 1 minuto).
export async function agregarCorreoACaso(c: Caso, hiloId: string, autor: string) {
  if (c.hiloId === hiloId || c.hilosUnidos.includes(hiloId)) return;
  const excluidos = await hilosExcluidosDe(c.id);
  const permitir = excluidos.includes(hiloId) ? { hilosExcluidos: excluidos.filter((h) => h !== hiloId) } : {};
  await editarCaso(c.id, c.hiloId ? { hilosUnidos: [...c.hilosUnidos, hiloId], notasInternas: [...c.notas, nota(autor, 'Se agregó un correo a este caso.')], ...permitir } : { hiloId, ...permitir });
}

// Hilos separados de un caso. Si el campo aun no existe (app sin publicar), ninguno.
const hilosExcluidosDe = async (casoId: string) =>
  (await gqlTodos<{ hilosExcluidos: string[] | null }>('casos', 'hilosExcluidos', { filtro: { id: { eq: casoId } }, max: 1 }).catch(() => []))[0]?.hilosExcluidos ?? [];

// Buscar casos (para unir o agregar un correo): con el buscador en toda la conversacion (D45); si no responde, por
// numero, asunto o correo.
export async function buscarCasos(q: string) {
  const t = q.trim();
  if (!t) return [];
  const b = await buscarEnCasos(t, 15);
  if (b) return b.ids.length ? enOrden(await gqlTodos<RawCaso>('casos', CAMPOS_CASO, { filtro: { id: { in: b.ids } }, max: 15 }), b.ids).map(aCaso) : [];
  const n = Number(t.replace(/^#/, ''));
  const filtro = { or: [{ name: { ilike: `%${t}%` } }, { correoCliente: { ilike: `%${t}%` } }, ...(Number.isFinite(n) && n > 0 ? [{ numero: { eq: n } }] : [])] };
  return (await gqlTodos<RawCaso>('casos', CAMPOS_CASO, { filtro, orden: [{ ultimoMensaje: 'DescNullsLast' }], max: 15 })).map(aCaso);
}

// Cambiar varios casos a la vez (p. ej. mover a un grupo o cerrar los casos sin grupo del CRM).
export async function actualizarCasos(ids: string[], data: Record<string, unknown>) {
  for (let i = 0; i < ids.length; i += 100) {
    await gql('mutation($f:CasoFilterInput!,$d:CasoUpdateInput!){ updateCasos(filter:$f, data:$d){ id } }', { f: { id: { in: ids.slice(i, i + 100) } }, d: data });
  }
}

// Al renombrar un grupo (Administracion -> Grupos) sus casos pasan al nombre nuevo. Devuelve cuantos cambiaron.
// De a 100: Twenty no deja cambiar mas de 200 registros en una sola peticion (el grupo "Juan" tiene mas de 3.000 casos).
export async function renombrarGrupoDeCasos(antes: string, despues: string) {
  const ids = (await gqlTodos<{ id: string }>('casos', 'id', { filtro: { grupo: { eq: antes } }, max: 50_000 })).map((c) => c.id);
  await actualizarCasos(ids, { grupo: despues });
  return ids.length;
}
